import { CheckCircle2, Clipboard, Download, FileArchive, Info, LoaderCircle, TriangleAlert } from "lucide-react";
import {
  effectiveFormat,
  normalizePlaylistFilename,
  type CompatibilityNotice,
  type PlaylistDocument,
  type PlaylistFormat,
  type ProTVTarget,
} from "@protv-converter/core";

interface ExportPanelProps {
  document: PlaylistDocument;
  target: ProTVTarget;
  format: PlaylistFormat;
  filename: string;
  urlPrefix: string;
  bundleThumbnails: boolean;
  notices: CompatibilityNotice[];
  preview: string;
  isExporting: boolean;
  exportProgress: string;
  onTargetChange: (target: ProTVTarget) => void;
  onFormatChange: (format: PlaylistFormat) => void;
  onFilenameChange: (value: string) => void;
  onUrlPrefixChange: (value: string) => void;
  onBundleChange: (value: boolean) => void;
  onCopy: () => void;
  onExport: () => Promise<void>;
}

const targets: Array<{ id: ProTVTarget; label: string; detail: string }> = [
  { id: "3.x", label: "ProTV 3.x", detail: "Current title and description tokens; JSON available." },
  { id: "2.3", label: "ProTV 2.3", detail: "Legacy titles with alternate URLs and tags." },
  { id: "2.2", label: "Legacy 2.2", detail: "Main URLs, image paths, and titles only." },
];

export function ExportPanel({
  document,
  target,
  format,
  filename,
  urlPrefix,
  bundleThumbnails,
  notices,
  preview,
  isExporting,
  exportProgress,
  onTargetChange,
  onFormatChange,
  onFilenameChange,
  onUrlPrefixChange,
  onBundleChange,
  onCopy,
  onExport,
}: ExportPanelProps) {
  const actualFormat = effectiveFormat(target, format);
  const youtubeEntryCount = document.entries.filter((entry) => entry.youtubeVideoId).length;

  return (
    <section aria-labelledby="export-title" className="flex h-full min-h-0 flex-col border-line bg-paper lg:border-l">
      <div className="border-b border-line px-4 py-4">
        <p className="eyebrow">Export</p>
        <h2 id="export-title" className="mt-1 text-lg font-semibold text-ink">Choose the target</h2>
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-5">
        <fieldset>
          <legend className="field-label">ProTV version</legend>
          <div className="space-y-2">
            {targets.map((option) => (
              <label className={`flex cursor-pointer items-start gap-3 border px-3 py-3 transition-colors ${target === option.id ? "border-accent bg-accent-soft" : "border-line bg-paper hover:border-line-strong"}`} key={option.id}>
                <input className="mt-0.5 size-4 accent-accent" type="radio" name="target" value={option.id} checked={target === option.id} onChange={() => onTargetChange(option.id)} />
                <span className="min-w-0"><span className="block text-sm font-semibold text-ink">{option.label}</span><span className="mt-0.5 block text-xs leading-relaxed text-muted">{option.detail}</span></span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="field-label">File format</legend>
          <div className="grid grid-cols-2 border border-line">
            {(["tokenized", "json"] as const).map((value) => {
              const disabled = target !== "3.x" && value === "json";
              return (
                <label className={`flex min-h-11 items-center justify-center gap-2 border-r border-line px-3 text-sm font-semibold last:border-r-0 ${disabled ? "cursor-not-allowed bg-canvas text-muted/60" : "cursor-pointer"} ${actualFormat === value ? "bg-accent-soft text-accent" : "text-ink"}`} key={value}>
                  <input className="sr-only" type="radio" name="format" value={value} checked={actualFormat === value} disabled={disabled} onChange={() => onFormatChange(value)} />
                  {value === "tokenized" ? "Tokenized" : "JSON"}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div>
          <label className="field-label" htmlFor="export-filename">Filename</label>
          <input id="export-filename" className="field" value={filename} onChange={(event) => onFilenameChange(event.target.value)} />
          <p className="field-help">Downloads as {normalizePlaylistFilename(filename)}</p>
        </div>

        <details className="border-y border-line py-3">
          <summary className="cursor-pointer text-sm font-semibold text-ink">Advanced URL wrapper</summary>
          <div className="mt-3">
            <label className="field-label" htmlFor="url-prefix">Prefix every main URL</label>
            <input id="url-prefix" className="field" value={urlPrefix} onChange={(event) => onUrlPrefixChange(event.target.value)} placeholder="https://example.com/?url=" />
            <p className="field-help">Kept for compatibility with the original converter. The prefix is added only during export.</p>
          </div>
        </details>

        <label className="flex cursor-pointer items-start gap-3 border border-line bg-canvas px-3 py-3">
          <input className="mt-0.5 size-4 accent-accent" type="checkbox" checked={bundleThumbnails} onChange={(event) => onBundleChange(event.target.checked)} />
          <span><span className="flex items-center gap-1.5 text-sm font-semibold text-ink"><FileArchive aria-hidden="true" size={15} /> Bundle YouTube thumbnails</span><span className="mt-1 block text-xs leading-relaxed text-muted">Create a ZIP with the playlist and {youtubeEntryCount} available image {youtubeEntryCount === 1 ? "file" : "files"} under Assets/ProTV/PlaylistImages.</span></span>
        </label>

        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="field-label mb-0">Output preview</span>
            <button className="text-button" type="button" onClick={onCopy} disabled={!preview}><Clipboard aria-hidden="true" size={14} /> Copy</button>
          </div>
          <pre className="max-h-48 overflow-auto border border-line bg-code p-3 font-mono text-[0.68rem] leading-5 text-code-text">{preview || "Add entries to preview the playlist."}</pre>
        </div>

        <div aria-live="polite" className="space-y-2">
          {notices.map((notice) => (
            <div className="flex gap-2 border border-warning/35 bg-warning-soft px-3 py-2.5 text-xs leading-relaxed text-warning" key={`${notice.field}-${notice.message}`}>
              <TriangleAlert aria-hidden="true" className="mt-0.5 shrink-0" size={15} />
              <p><strong>{notice.count} {notice.count === 1 ? "entry" : "entries"}:</strong> {notice.message}</p>
            </div>
          ))}
          {document.entries.length > 0 && notices.length === 0 ? (
            <div className="flex gap-2 border border-valid/35 bg-valid-soft px-3 py-2.5 text-xs leading-relaxed text-valid">
              <CheckCircle2 aria-hidden="true" className="mt-0.5 shrink-0" size={15} /><p>All populated fields are supported by {targets.find((option) => option.id === target)?.label}.</p>
            </div>
          ) : null}
          {target === "3.x" ? (
            <a className="flex cursor-pointer items-start gap-2 text-xs leading-relaxed text-muted underline decoration-line-strong underline-offset-4 hover:text-ink" href="https://protv.dev/guides/playlist" target="_blank" rel="noreferrer"><Info aria-hidden="true" className="mt-0.5 shrink-0" size={14} /> Review ProTV’s current playlist file documentation.</a>
          ) : null}
        </div>
      </div>

      <div className="border-t border-line bg-paper p-4">
        {exportProgress ? <p className="mb-2 text-center text-xs text-muted" aria-live="polite">{exportProgress}</p> : null}
        <button className="primary-button w-full" type="button" onClick={() => void onExport()} disabled={isExporting || document.entries.length === 0}>
          {isExporting ? <LoaderCircle aria-hidden="true" className="animate-spin motion-reduce:animate-none" size={17} /> : <Download aria-hidden="true" size={17} />}
          {isExporting ? "Preparing download" : bundleThumbnails ? "Download thumbnail bundle" : "Download .playlist"}
        </button>
      </div>
    </section>
  );
}
